import { db } from '@/db/client';
import { TeamPresetSchema, type TeamPreset } from '@/schemas';

export interface MergeResult {
  applied: number;
  replaced: number;
}

/**
 * Compare two team presets for last-write-wins conflict resolution.
 * Returns true if incoming preset should replace existing preset.
 */
function shouldReplaceTeamPreset(existing: TeamPreset, incoming: TeamPreset): boolean {
  // Use updated_at for conflict resolution (last-write-wins)
  if (incoming.updatedAt !== existing.updatedAt) {
    return incoming.updatedAt > existing.updatedAt;
  }

  // If updated_at is equal, use created_at as tie-breaker
  if (incoming.createdAt !== existing.createdAt) {
    return incoming.createdAt > existing.createdAt;
  }

  // If both are equal, use ID as final tie-breaker (deterministic)
  return incoming.id > existing.id;
}

export async function mergeRemoteTeamPresets(incoming: TeamPreset[]): Promise<MergeResult> {
  if (incoming.length === 0) return { applied: 0, replaced: 0 };
  const validated = incoming.map((p) => TeamPresetSchema.parse(p));
  let applied = 0;
  let replaced = 0;

  await db.transaction('rw', db.teamPresets, async () => {
    for (const preset of validated) {
      const existing = await db.teamPresets.get(preset.id);
      if (existing) {
        // Conflict resolution: last-write-wins (updated_at)
        if (shouldReplaceTeamPreset(existing, preset)) {
          await db.teamPresets.put(preset);
          replaced += 1;
        }
        // Otherwise keep existing preset
      } else {
        await db.teamPresets.add(preset);
        applied += 1;
      }
    }
  });

  return { applied, replaced };
}
